import { useState, useEffect, useRef } from "react";
import { format, parseISO, addDays, subDays, startOfWeek, isToday, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar,
  Clock,
  Check,
  RotateCcw,
  Trash2,
  Copy,
  Edit,
  MoreVertical,
  FileText,
  Target,
  Users,
  Star,
  Sparkles,
  Sun,
  Moon,
  CalendarDays,
} from "lucide-react";
import { getMatiereInfo, getSessionTypeInfo, JournalStatusBadge } from "./constants";

// Mobile Session Card - Optimized for touch
function MobileSessionCard({ session, onEdit, onStatusChange, onDelete, onDuplicate }) {
  const matiereInfo = getMatiereInfo(session.matiere);
  const sessionTypeInfo = getSessionTypeInfo(session.session_type);
  const Icon = matiereInfo?.icon;
  const hasSkills = session.skills?.length > 0;
  const hasResource = !!session.resource_id;
  
  const timeDisplay = session.start_time && session.end_time 
    ? `${session.start_time} - ${session.end_time}`
    : session.start_time || "";

  return (
    <div
      data-testid={`mobile-session-${session.id}`}
      className="bg-white rounded-xl shadow-sm border-l-4 overflow-hidden active:scale-[0.98] transition-transform"
      style={{ borderLeftColor: matiereInfo?.color }}
      onClick={() => onEdit(session)}
    >
      <div className="p-3">
        {/* Header with time and status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${matiereInfo?.color}20` }}
            >
              {Icon && <Icon className="w-4 h-4" style={{ color: matiereInfo?.color }} />}
            </div>
            <div>
              <span className="text-xs text-slate-500 font-medium">{timeDisplay}</span>
              <Badge variant="outline" className={`ml-2 text-[10px] px-1.5 py-0 ${sessionTypeInfo?.color}`}>
                {sessionTypeInfo?.label}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <JournalStatusBadge status={session.status} className="text-[10px]" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <MoreVertical className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange(session.id, "fait"); }}>
                  <Check className="w-4 h-4 mr-2 text-green-600" />
                  Marquer fait
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onStatusChange(session.id, "reporte"); }}>
                  <RotateCcw className="w-4 h-4 mr-2 text-orange-600" />
                  Reporter
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(session.id); }}>
                  <Copy className="w-4 h-4 mr-2 text-blue-600" />
                  Dupliquer
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={(e) => { e.stopPropagation(); onDelete(session.id); }}
                  className="text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-slate-800 mb-1" style={{ color: matiereInfo?.color }}>
          {session.title}
        </h3>

        {/* Matière label */}
        <p className="text-xs text-slate-500 mb-2">
          {session.matiere_label || matiereInfo?.label}
          {session.niveau && ` • ${session.niveau.toUpperCase()}`}
        </p>

        {/* Main skill if present */}
        {session.main_skill && (
          <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-amber-50 px-2 py-1 rounded-md mb-2">
            <Star className="w-3 h-3 text-amber-500" />
            <span className="truncate">{session.main_skill.label}</span>
          </div>
        )}

        {/* Resource if present */}
        {session.resource_title && (
          <div className="flex items-center gap-1.5 text-xs text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md">
            <FileText className="w-3 h-3" />
            <span className="truncate">{session.resource_title}</span>
          </div>
        )}

        {/* Indicators */}
        <div className="flex items-center gap-2 mt-2">
          {hasSkills && (
            <div className="flex items-center gap-1 text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
              <Target className="w-3 h-3" />
              {session.skills.length}
            </div>
          )}
          {session.has_differentiation && (
            <div className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
              <Users className="w-3 h-3" />
              Diff.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Day Picker Component
function DayPicker({ selectedDate, onDateChange, weekDates, getDateStatus }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2 scrollbar-hide">
      {weekDates.filter(d => d.isWorking).map((dayInfo) => {
        const date = parseISO(dayInfo.date);
        const isSelected = isSameDay(date, selectedDate);
        const dateStatus = getDateStatus(dayInfo.date);
        const today = isToday(date);
        
        return (
          <button
            key={dayInfo.date}
            onClick={() => onDateChange(date)}
            className={`flex flex-col items-center min-w-[52px] py-2 px-2 rounded-xl transition-all ${
              isSelected 
                ? "bg-indigo-600 text-white shadow-md" 
                : dateStatus.isHoliday
                  ? "bg-orange-50 text-orange-600"
                  : today
                    ? "bg-indigo-50 text-indigo-700"
                    : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <span className="text-[10px] uppercase font-medium opacity-80">
              {format(date, "EEE", { locale: fr })}
            </span>
            <span className={`text-lg font-bold ${isSelected ? "" : ""}`}>
              {format(date, "d")}
            </span>
            {today && !isSelected && (
              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-0.5" />
            )}
            {dateStatus.isHoliday && (
              <div className="w-1.5 h-1.5 rounded-full bg-orange-500 mt-0.5" />
            )}
          </button>
        );
      })}
    </div>
  );
}

// Main Mobile Journal View Component
export function MobileJournalView({
  weekDates,
  sessionsByDate,
  selectedDate,
  onDateChange,
  onAddSession,
  onEditSession,
  onStatusChange,
  onDelete,
  onDuplicate,
  getDateStatus,
  timeSlots,
  loading,
}) {
  const [touchStart, setTouchStart] = useState(null);
  const scrollRef = useRef(null);
  
  const currentDateStr = format(selectedDate, "yyyy-MM-dd");
  const sessions = sessionsByDate[currentDateStr] || [];
  const dateStatus = getDateStatus(currentDateStr);
  const today = isToday(selectedDate);

  // Group sessions by morning/afternoon
  const morningSessions = sessions.filter(s => {
    const hour = parseInt(s.start_time?.split(":")[0] || "0");
    return hour < 12;
  });
  const afternoonSessions = sessions.filter(s => {
    const hour = parseInt(s.start_time?.split(":")[0] || "12");
    return hour >= 12;
  });

  // Navigate to previous/next day
  const goToPrevDay = () => {
    const workingDays = weekDates.filter(d => d.isWorking);
    const currentIndex = workingDays.findIndex(d => d.date === currentDateStr);
    if (currentIndex > 0) {
      onDateChange(parseISO(workingDays[currentIndex - 1].date));
    }
  };

  const goToNextDay = () => {
    const workingDays = weekDates.filter(d => d.isWorking);
    const currentIndex = workingDays.findIndex(d => d.date === currentDateStr);
    if (currentIndex < workingDays.length - 1) {
      onDateChange(parseISO(workingDays[currentIndex + 1].date));
    }
  };

  // Touch handlers for swipe navigation
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goToNextDay();
      } else {
        goToPrevDay();
      }
    }
    setTouchStart(null);
  };

  const renderSessionList = (sessionList, period) => {
    if (sessionList.length === 0) {
      return (
        <div className="text-center py-6 text-slate-400">
          <p className="text-sm">Aucune séance</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {sessionList.map((session) => (
          <MobileSessionCard
            key={session.id}
            session={session}
            onEdit={onEditSession}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Week Navigation */}
      <div className="px-4 pt-2">
        <div className="flex items-center justify-between mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevDay}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="text-center">
            <h3 className="font-semibold text-slate-800">
              {format(selectedDate, "EEEE d MMMM", { locale: fr })}
            </h3>
            {today && (
              <Badge className="bg-indigo-100 text-indigo-700 mt-1">Aujourd'hui</Badge>
            )}
            {dateStatus.isHoliday && (
              <Badge className="bg-orange-100 text-orange-700 mt-1">{dateStatus.name}</Badge>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextDay}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Day Picker */}
        <DayPicker
          selectedDate={selectedDate}
          onDateChange={onDateChange}
          weekDates={weekDates}
          getDateStatus={getDateStatus}
        />
      </div>

      {/* Sessions Content */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 pb-20"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {dateStatus.isHoliday ? (
          <div className="flex flex-col items-center justify-center h-64 text-orange-500">
            <CalendarDays className="w-12 h-12 mb-2 opacity-50" />
            <p className="font-medium">{dateStatus.name}</p>
            <p className="text-sm text-orange-400">Pas de cours</p>
          </div>
        ) : (
          <>
            {/* Morning Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-gradient-to-b from-slate-50 to-transparent py-2 z-10">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                  <Sun className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-700">Matin</h4>
                  <p className="text-xs text-slate-400">{morningSessions.length} séance(s)</p>
                </div>
              </div>
              {renderSessionList(morningSessions, "morning")}
            </div>

            {/* Afternoon Section */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3 sticky top-0 bg-gradient-to-b from-slate-50 to-transparent py-2 z-10">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Moon className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-700">Après-midi</h4>
                  <p className="text-xs text-slate-400">{afternoonSessions.length} séance(s)</p>
                </div>
              </div>
              {renderSessionList(afternoonSessions, "afternoon")}
            </div>
          </>
        )}
      </div>

      {/* Floating Action Button */}
      {!dateStatus.isHoliday && (
        <div className="fixed bottom-20 right-4 z-50">
          <Button
            onClick={() => onAddSession(currentDateStr, null)}
            className="h-14 w-14 rounded-full shadow-lg bg-indigo-600 hover:bg-indigo-700"
            data-testid="mobile-add-session-btn"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </div>
      )}

      {/* Bottom Stats Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 z-40">
        <div className="flex items-center justify-around text-center">
          <div>
            <p className="text-lg font-bold text-slate-800">{sessions.length}</p>
            <p className="text-[10px] text-slate-500">Séances</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div>
            <p className="text-lg font-bold text-green-600">
              {sessions.filter(s => s.status === "fait").length}
            </p>
            <p className="text-[10px] text-slate-500">Faites</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div>
            <p className="text-lg font-bold text-blue-600">
              {sessions.filter(s => s.status === "prevu").length}
            </p>
            <p className="text-[10px] text-slate-500">Prévues</p>
          </div>
          <div className="w-px h-8 bg-slate-200" />
          <div>
            <p className="text-lg font-bold text-orange-600">
              {sessions.filter(s => s.status === "reporte").length}
            </p>
            <p className="text-[10px] text-slate-500">Reportées</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileJournalView;
